import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { useIsAuthenticated, useMfaRequired, useAuthStore, useSessionTimeoutWarning } from '@/app/store/auth'
import { authApi } from '@/lib/api/clients/auth'
import { useSessionTimeout, useUserActivity } from '@/hooks/useSessionTimeout'
import { useAuthInitialization } from '@/hooks/useAuthInitialization'
import { AppShell } from '@/components/layout/AppShell'
import { SessionTimeoutDialog } from '@/components/dialogs/SessionTimeoutDialog'
import { Screensaver } from '@/components/Screensaver'
import { useIdleTimer } from '@/hooks/useIdleTimer'
import { useIdleSettings, IDLE_FALLBACK } from '@/hooks/useIdleSettings'
import { useLockStore } from '@/app/store/lock'
import { useDisplayStore } from '@/app/store/display'
import { LoginPage } from '@/pages/LoginPage'
import { MfaPage } from '@/pages/MfaPage'
import { StepUpGate } from '@/features/devices/components/StepUpGate'
import { MobilisePage } from '@/features/mobilisation/pages/MobilisePage'
import { MyTeamPage } from '@/features/mobilisation/pages/MyTeamPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { DesignSystemPage } from '@/pages/DesignSystemPage'
import { PaletteEditorPage } from '@/pages/PaletteEditorPage'
import { SimpleProfilePage } from '@/features/profile/pages/SimpleProfilePage'
import { SettingsPage } from '@/features/settings/pages/SettingsPage'
import SecurityDashboardPage from '@/features/security/pages/SecurityDashboardPage'
import LoginActivityPage from '@/features/security/pages/LoginActivityPage'
import LogsPage from '@/features/logs/pages/LogsPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { VaultListPage } from '@/features/vaults/pages/VaultListPage'
import { JobListPage } from '@/features/jobs/pages/JobListPage'
import { UserListPage } from '@/features/users/pages/UserListPage'
import { IconsAdminPage } from '@/features/icons/pages/IconsAdminPage'
import { ShapesAdminPage } from '@/features/shapes/pages/ShapesAdminPage'
import { SchedulePage } from '@/features/visualization/pages/SchedulePage'
import { ClassificationListPage } from '@/features/classifications/pages/ClassificationListPage'
import { TemplateListPage } from '@/features/templates/pages/TemplateListPage'
import { TemplateEditorPage } from '@/features/templates/pages/TemplateEditorPage'
import { RoleListPage } from '@/features/roles/pages/RoleListPage'
import { LocationListPage } from '@/features/locations/pages/LocationListPage'
import { ObjectListPage } from '@/features/objects/pages/ObjectListPage'
import { ObjectViewPage } from '@/features/objects/pages/ObjectViewPage'
import { TeamListPage } from '@/features/teams/pages/TeamListPage'
import { TechnologyListPage } from '@/features/technologies/pages/TechnologyListPage'
import { ContactListPage } from '@/features/contacts/pages/ContactListPage'
import { MasterRunbookListPage } from '@/features/master-runbooks/pages/MasterRunbookListPage'
import { ServiceNowCMDBPage } from '@/features/servicenow-cmdb/pages/ServiceNowCMDBPage'
import { RecoverRequestListPage } from '@/features/recover-requests/pages/RecoverRequestListPage'
import { RequestApprovalListPage } from '@/features/request-approvals/pages/RequestApprovalListPage'
import { ScanStatusChangesPage } from '@/features/scan-status-changes/pages/ScanStatusChangesPage'
import { MyAssignmentsPage } from '@/features/my-assignments/pages/MyAssignmentsPage'
import { UpdateRunBookPage } from '@/features/update-runbook/pages/UpdateRunBookPage'
import { RecoveryRunbookPage } from '@/features/recover-requests/pages/RecoveryRunbookPage'
import { RecoveryManagementPage } from '@/features/recover-requests/pages/RecoveryManagementPage'
import { RecoveryScenariosPage } from '@/features/recover-requests/pages/RecoveryScenariosPage'
import { RecoverySetDetailPage } from '@/features/recover-requests/pages/RecoverySetDetailPage'
import { ImpactAssessmentCanvasPage } from '@/features/recover-requests/pages/ImpactAssessmentCanvasPage'
import { ImpactAssessmentPage } from '@/features/recover-requests/pages/ImpactAssessmentPage'
import { RestoreAgentTestPage } from '@/features/restore-agent/pages/RestoreAgentTestPage'
import { UnassignedRequestDetailPage } from '@/features/recover-requests/pages/UnassignedRequestDetailPage'
import { AiModelsPage } from '@/features/ai-models/pages/AiModelsPage'
import { KnowledgeBasePage } from '@/features/knowledge-base/pages/KnowledgeBasePage'
import { ForensicsListPage } from '@/features/forensics/pages/ForensicsListPage'
// ModelChangesPage now integrated as tab in AiModelsPage
import { IngressMonitoringPage } from '@/features/ingress-monitoring/pages/IngressMonitoringPage'
import { IaCPage } from '@/features/iac/pages/IaCPage'
import { IaCListPage } from '@/features/iac/pages/IaCListPage'
import { AnalyticsPage } from '@/features/analytics/pages/AnalyticsPage'
import { SchedulersPage } from '@/features/schedulers/pages/SchedulersPage'
import { PermissionRoute } from '@/components/auth/PermissionRoute'
import { DisplayRunnerPage } from '@/features/displays/pages/DisplayRunnerPage'
import { DisplayAdminPage } from '@/features/displays/pages/DisplayAdminPage'

/**
 * Remounts IaCPage whenever the project identity changes. The route param is
 * only `:mainId/:scenario`, but a project is identified by the `?pid=` query —
 * so switching projects (e.g. creating a new one from the list) changes only
 * the query string and would otherwise reuse the mounted component, leaking the
 * previous project's chat/files/stepper state. Keying on path+search forces a
 * clean remount (fresh state) per project. IaCPage never mutates `pid`
 * mid-run, so this never interrupts an in-progress generation.
 */
function IaCPageKeyed() {
    const location = useLocation()
    return <IaCPage key={`${location.pathname}${location.search}`} />
}

// Lazy-loaded feature modules (visualization + AI chat)
const VisualizationPage = lazy(() =>
    import('@/features/visualization/pages/VisualizationPage').then((m) => ({
        default: m.VisualizationPage,
    }))
)
const AiChatPage = lazy(() =>
    import('@/features/ai-chat/pages/AiChatPage').then((m) => ({
        default: m.AiChatPage,
    }))
)
const GuestJoinPage = lazy(
    () => import('@/features/ai-chat/core/components/video/GuestJoinPage')
)

/**
 * Guest join — the target of the invite link the backend mints for external
 * participants (`${APP_BASE_URL}/join/<token>`). Public: the guest has no
 * account, and the /guest/validate + /guest/join endpoints authenticate on the
 * token instead. GuestJoinPage takes the token as a prop, so read it here.
 */
function GuestJoinRoute() {
    const { token } = useParams()
    return <GuestJoinPage token={token} />
}

// Protected route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const isAuthenticated = useIsAuthenticated()
    const mfaRequired = useMfaRequired()
    const { accessToken, refreshToken } = useAuthStore((state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
    }))

    // Handle session timeout for authenticated users
    useSessionTimeout()
    useUserActivity()

    // Check if we have isAuthenticated but no valid tokens (inconsistent state)
    if (isAuthenticated && (!accessToken || !refreshToken)) {
        return <Navigate to="/login" replace />
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />
    }

    if (mfaRequired) {
        return <Navigate to="/mfa" replace />
    }

    return <AppShell>{children}</AppShell>
}

// Protected route WITHOUT AppShell — for standalone pages (e.g. AI chat in new tab)
function StandaloneProtectedRoute({ children }: { children: React.ReactNode }) {
    const isAuthenticated = useIsAuthenticated()
    const mfaRequired = useMfaRequired()
    const { accessToken, refreshToken } = useAuthStore((state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
    }))

    useSessionTimeout()
    useUserActivity()

    if (isAuthenticated && (!accessToken || !refreshToken)) {
        return <Navigate to="/login" replace />
    }
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />
    }
    if (mfaRequired) {
        return <Navigate to="/mfa" replace />
    }

    return <>{children}</>
}

// Public route component (redirects to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
    const isAuthenticated = useIsAuthenticated()
    const mfaRequired = useMfaRequired()

    if (isAuthenticated && !mfaRequired) {
        return <Navigate to="/" replace />
    }

    return <>{children}</>
}

export default function App() {
    // Initialize and validate authentication state on app startup
    useAuthInitialization()

    // Session timeout warning state
    const sessionTimeoutWarning = useSessionTimeoutWarning()
    const isAuthenticated = useIsAuthenticated()

    // ── Idle behaviour ────────────────────────────────────────────────────────────
    // TWO TIMERS, DELIBERATELY SEPARATE. The screensaver hides the screen after a short idle and
    // is dismissed by any activity with the session intact; signing out is a much longer, distinct
    // timeout. Previously a quiet screen only ever produced the second, which in an operations
    // room means returning to a login form mid-incident.
    const { data: idleSettings } = useIdleSettings()
    const idle = idleSettings ?? IDLE_FALLBACK
    // The lock lives in its own store: the idle timer raises it here, and the user's own menu
    // raises it from three levels away. One flag, two sources.
    const locked = useLockStore((st) => st.locked)
    const lockVerified = useLockStore((st) => st.verified)
    const lock = useLockStore((st) => st.lock)
    const unlockScreen = useLockStore((st) => st.unlock)

    /**
     * A LOCK RESTORED FROM STORAGE PROVES NOTHING ABOUT THE SESSION UNDER IT.
     *
     * `locked` is persisted so a reload cannot defeat it, and `isAuthenticated` is persisted too —
     * it stays true while expired tokens sit in localStorage. Together they showed a lock screen,
     * days later, telling someone their session was still open and that unlocking would return
     * them to where they were. Both were false, and the login screen was what they wanted.
     *
     * So on the first render of a restored lock, ask the server. Alive: the lock is real, keep it.
     * Gone: drop the lock and let the normal unauthenticated flow show the login screen.
     */
    useEffect(() => {
        if (!locked || lockVerified || !isAuthenticated) return
        let cancelled = false
        void (async () => {
            try {
                await authApi.me()
                if (!cancelled) useLockStore.getState().markVerified()
            } catch {
                // Includes the case where the axios interceptor already tried to refresh and could
                // not. Unlock BEFORE logout so nothing renders a lock over a cleared session.
                if (cancelled) return
                useLockStore.getState().unlock()
                useAuthStore.getState().logout()
            }
        })()
        return () => { cancelled = true }
    }, [locked, lockVerified, isAuthenticated])
    // A command-and-control display is exempt from both timers. Not because of who is signed
    // in — a role a person can hold would follow that person to their laptop — but because
    // this browser is enrolled as a display, and a screen on a wall in a controlled room has
    // nobody to unlock it. The server grants the display session nothing extra: it refuses
    // every non-GET from it regardless, so this flag can only ever stop a lock, never permit
    // a change.
    const isDisplay = useDisplayStore((st) => st.isDisplay)
    const { reached, markActive } = useIdleTimer({
        enabled: isAuthenticated && !isDisplay,
        thresholds: {
            screensaver: idle.screensaverEnabled && !isDisplay ? idle.screensaverMinutes * 60_000 : 0,
            logout: isDisplay ? 0 : idle.logoutMinutes * 60_000,
        },
    })

    useEffect(() => { if (reached.screensaver && !isDisplay) lock(false) }, [reached.screensaver, lock, isDisplay])

    useEffect(() => {
        if (!reached.logout || !isAuthenticated || isDisplay) return
        // The longer timer still ends the session — the lock replaces the SHORT logout, it does
        // not remove logging out altogether.
        unlockScreen()
        useAuthStore.getState().logout()
    }, [reached.logout, isAuthenticated, unlockScreen, isDisplay])
    const { extendSession, logout } = useAuthStore()

    const handleExtendSession = () => {
        extendSession()
    }

    const handleLogout = () => {
        logout()
    }

    return (
        <>
            {/* Asks "confirm it's you" when a high-risk action needs a fresh proof of a device. It
                does nothing until asked, and only a signed-in user can be asked. */}
            {isAuthenticated && <StepUpGate />}
            <Routes>
                {/* Public routes */}
                {/* The command-and-control wall. Public because the device has no session
                    yet — its enrolment token IS the credential, and it is entered once on
                    the machine itself. Outside the app shell: a wall screen wants the view,
                    not the navigation. */}
                <Route path="/display" element={<DisplayRunnerPage />} />
                <Route
                    path="/login"
                    element={
                        <PublicRoute>
                            <LoginPage />
                        </PublicRoute>
                    }
                />
                <Route
                    path="/mfa"
                    element={
                        <PublicRoute>
                            <MfaPage />
                        </PublicRoute>
                    }
                />
                {/* A standby user called in for a recovery sets themselves up here: the code texted to
                    them, a new password, a sign-in device. It never signs anyone in. */}
                <Route
                    path="/mobilise"
                    element={
                        <PublicRoute>
                            <MobilisePage />
                        </PublicRoute>
                    }
                />

                {/* Protected routes. Each checks the permission its API checks (docs/rbac-permissions.md,
                    "Screens -> permission"), never a role name: a role is only a set of permissions,
                    and the Roles screen is where they are granted. /profile-settings is personal. */}
                <Route
                    path="/"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="dashboard.read">
                                <DashboardPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />

                {/* Placeholder routes for future features */}
                <Route
                    path="/jobs"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="jobs.read">
                                <JobListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/policies"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="jobs.read">
                                <div className="p-6">
                                    <h1 className="text-2xl font-bold">Policies</h1>
                                    <p className="text-muted-foreground">Policy management coming soon...</p>
                                </div>
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/targets"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="jobs.read">
                                <div className="p-6">
                                    <h1 className="text-2xl font-bold">Targets</h1>
                                    <p className="text-muted-foreground">Target management coming soon...</p>
                                </div>
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/vaults"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="vaults.read">
                                <VaultListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/users"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="users.update">
                                <UserListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                {/* A team's lead or deputy, during a mobilisation: the people waiting to be let in. */}
                <Route
                    path="/my-team"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="mobilisation.confirm">
                                <MyTeamPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/displays"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="system.admin">
                                <DisplayAdminPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                
                <Route
                    path="/classifications"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="classifications.read">
                                <ClassificationListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/templates"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="templates.read">
                                <TemplateListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                {/* Authoring a template is only reachable from the Templates screen — never a
                    toggle on the planning canvas, where every node is a real object. */}
                <Route
                    path="/templates/new"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="templates.create">
                                <TemplateEditorPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                {/* Preview and edit are the same page, keyed apart: React Router keeps a component
                    mounted across sibling routes that render the same type, so without the keys
                    Back from an editor with unsaved changes would show those changes as the
                    preview of the saved template. */}
                <Route
                    path="/templates/:id"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="templates.read">
                                <TemplateEditorPage key="preview" mode="preview" />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/templates/:id/edit"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="templates.update">
                                <TemplateEditorPage key="edit" />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/locations"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="locations.read">
                                <LocationListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/roles"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="roles.update">
                                <RoleListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/teams"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="teams.read">
                                <TeamListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/technologies"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="technologies.read">
                                <TechnologyListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/icons"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="graphics.read">
                                <IconsAdminPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/graphics"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="graphics.read">
                                <ShapesAdminPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                {/* The library was called Shapes until 2026-09-08. A bookmark or a link in
                    someone's notes should not 404 because the product renamed a screen. */}
                <Route path="/shapes" element={<Navigate to="/graphics" replace />} />
                <Route
                    path="/contacts"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="contacts.read">
                                <ContactListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/master-runbooks"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="master-runbooks.read">
                                <MasterRunbookListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/servicenow-cmdb"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="servicenow.read">
                                <ServiceNowCMDBPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/ai-models"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="system.admin">
                                <AiModelsPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                {/* The customer knowledge base. It replaced the empty "AI Knowledge Base" page. */}
                <Route
                    path="/knowledge-base"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="knowledge.read">
                                <KnowledgeBasePage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route path="/ai-knowledge-base" element={<Navigate to="/knowledge-base" replace />} />
                <Route
                    path="/forensics"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="forensics.read">
                                <ForensicsListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/analytics"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="analytics.read">
                                <AnalyticsPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/ingress-monitoring"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="analytics.read">
                                <IngressMonitoringPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/recovery-management"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="recover-requests.read">
                                <RecoveryManagementPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/recovery-scenarios"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="recover-requests.update">
                                <RecoveryScenariosPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/recovery-management/requests/:requestID"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="recover-requests.read">
                                <UnassignedRequestDetailPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                {/* The restore agent's self-test, driven through the browser plug-in (13 Oct 2026). */}
                <Route
                    path="/restore-agent-test"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="recovery.execute">
                                <RestoreAgentTestPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/impact-assessment"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="recover-requests.read">
                                <ImpactAssessmentPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/impact-assessment/:assessmentId/canvas"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="recover-requests.read">
                                <ImpactAssessmentCanvasPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/recovery-management/sets/:setId"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="recover-requests.read">
                                <RecoverySetDetailPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/assign-engineer"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="recover-requests.update">
                                <RecoverRequestListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/scan-status-changes"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="scan-status.approve">
                                <ScanStatusChangesPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/request-approvals"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="recovery.approve">
                                <RequestApprovalListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/my-assignments"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="recover-requests.read">
                                <MyAssignmentsPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/update-runbook"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="recover-requests.read">
                                <UpdateRunBookPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/recovery-runbook/:requestId"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="recover-requests.read">
                                <RecoveryRunbookPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/objects"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="objects.read">
                                <ObjectListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/objects/:id"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="objects.read">
                                <ObjectViewPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/design-system"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="system.admin">
                                <DesignSystemPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/palette-editor"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="system.admin">
                                <PaletteEditorPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/security"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="security.read">
                                <SecurityDashboardPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/logs"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="system.admin">
                                <LogsPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/login-activity"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="system.admin">
                                <LoginActivityPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/profile-settings"
                    element={
                        <ProtectedRoute>
                            <SimpleProfilePage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/settings"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="system.admin">
                                <SettingsPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />

                {/* Infrastructure as Code */}
                <Route
                    path="/iac"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="iac.use">
                                <IaCListPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/iac/:mainId/:scenario"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="iac.use">
                                <IaCPageKeyed />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />

                {/* Schedulers */}
                <Route
                    path="/schedulers"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="schedulers.read">
                                <SchedulersPage />
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />

                {/* Visualization */}
                <Route
                    path="/visualization"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="canvas.view">
                                <Suspense fallback={<div className="flex h-full items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
                                    <VisualizationPage />
                                </Suspense>
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/schedule"
                    element={
                        <ProtectedRoute>
                            <PermissionRoute permission="recover-requests.read">
                                <Suspense fallback={<div className="flex h-full items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
                                    <SchedulePage />
                                </Suspense>
                            </PermissionRoute>
                        </ProtectedRoute>
                    }
                />

                {/* AI Chat — standalone (no sidebar/topbar) for open-in-new-tab */}
                <Route
                    path="/ai-chat"
                    element={
                        <StandaloneProtectedRoute>
                            <PermissionRoute permission="ai.use">
                                <Suspense fallback={<div className="flex h-full items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
                                    <AiChatPage />
                                </Suspense>
                            </PermissionRoute>
                        </StandaloneProtectedRoute>
                    }
                />

                {/* Video call guest join — public, reached from an invite link */}
                <Route
                    path="/join/:token"
                    element={
                        <Suspense fallback={<div className="flex h-full items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
                            <GuestJoinRoute />
                        </Suspense>
                    }
                />

                {/* 404 */}
                <Route path="*" element={<NotFoundPage />} />
            </Routes>

            {/* Session Timeout Dialog */}
            <SessionTimeoutDialog
                isOpen={sessionTimeoutWarning.isActive}
                onExtendSession={handleExtendSession}
                onLogout={handleLogout}
                timeRemaining={sessionTimeoutWarning.timeRemaining}
            />
            <Screensaver
                // Not shown until the session under it has been confirmed on this page load —
                // otherwise a stale lock flashes up over a session that no longer exists.
                active={locked && lockVerified && isAuthenticated}
                onUnlocked={() => { markActive(); unlockScreen() }}
            />
        </>
    )
}
